// Admin CertificatesPage — issuance overview + manual generation by enrollment.
import { useState } from "react";
import type { FormEvent } from "react";
import { useFetch, postJson } from "../../hooks/useApi.js";
import StatCard from "../../components/shared/StatCard.js";
import type { AdminSnapshot } from "@dm-lms/shared/types";

interface GeneratedCertificate {
  id: string;
  code: string;
  studentId: string;
  courseId: string;
  issuedAt: string;
}

export default function CertificatesPage() {
  const { data, isLoading, error, refetch } = useFetch<AdminSnapshot>("/api/admin/snapshot");
  const [enrollmentId, setEnrollmentId] = useState("");
  const [genError, setGenError] = useState<string | null>(null);
  const [genResult, setGenResult] = useState<GeneratedCertificate | null>(null);
  const [generating, setGenerating] = useState(false);

  async function generate(e: FormEvent) {
    e.preventDefault();
    if (!enrollmentId) return;
    setGenerating(true);
    setGenError(null);
    setGenResult(null);

    const result = await postJson<GeneratedCertificate>(`/api/certificates/generate/${enrollmentId}`);
    setGenerating(false);
    if (!result.ok) {
      setGenError(result.error);
      return;
    }
    setGenResult(result.data);
    setEnrollmentId("");
    refetch();
  }

  return (
    <div className="dm-page">
      <h1 className="text-2xl font-semibold text-dm-navy mb-6">Certificates</h1>

      {isLoading && <p className="text-dm-slate text-sm">Loading…</p>}
      {error && <p className="text-red-600 text-sm">{error}</p>}

      {data && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
          <StatCard label="Certificates Issued" value={data.certificatesIssued} />
          <StatCard label="Total Enrollments" value={data.totalEnrollments} />
        </div>
      )}

      <form className="rounded-lg border border-gray-200 bg-white p-4 max-w-md" onSubmit={generate}>
        <h2 className="text-sm font-medium text-dm-navy mb-3">Generate certificate</h2>
        <label className="block text-xs text-dm-slate mb-1">Enrollment ID</label>
        <input
          className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm mb-3"
          value={enrollmentId}
          onChange={(e) => setEnrollmentId(e.target.value)}
          placeholder="Enrollment ID"
        />
        <button
          type="submit"
          className="bg-dm-gold text-dm-navy text-sm px-4 py-2 rounded-md font-medium disabled:opacity-50"
          disabled={!enrollmentId || generating}
        >
          {generating ? "Generating…" : "Generate"}
        </button>
        {genError && <p className="text-red-600 text-xs mt-2">{genError}</p>}
        {genResult && (
          <p className="text-green-700 text-xs mt-2">Certificate {genResult.code} issued for enrollment.</p>
        )}
      </form>
    </div>
  );
}
